import { Injectable } from '@angular/core';
import {
    CanActivate,
    Router,
    ActivatedRouteSnapshot,
    RouterStateSnapshot,
    CanActivateChild,
    CanLoad,
    Route,
    UrlTree
} from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { IGuardPermission } from '../model/guardPermission.interface';
import { BadisOauthLoginService } from '../badis-oauth-login.service';
import { navigateToPublicEntry } from '../../public-entry.util';
import { AppPermissionService } from '../../app-permission.service';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate, CanActivateChild, CanLoad {

    private permissionObjectKey = 'roles';
    private legacyPermissionObjectKey = 'role';

    constructor(
        private router: Router,
        private authService: BadisOauthLoginService,
        private permissionService: AppPermissionService) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> {
        const returnUrl = state.url;
        const permissionData = this.getPermissionData(route.data);
        return this.hasAuthUserAccessToThisRoute(permissionData, returnUrl);
    }

    canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> {
        const returnUrl = state.url;
        const permissionData = this.getPermissionData(childRoute.data);
        return this.hasAuthUserAccessToThisRoute(permissionData, returnUrl);
    }

    canLoad(route: Route): boolean | UrlTree | Observable<boolean | UrlTree> {
        if (route.data) {
            const permissionData = this.getPermissionData(route.data);
            const returnUrl = `/${route.path}`;
            return this.hasAuthUserAccessToThisRoute(permissionData, returnUrl);
        } else {
            return true;
        }
    }

    private hasAuthUserAccessToThisRoute(permissionData: IGuardPermission, returnUrl: string): boolean | UrlTree | Observable<boolean | UrlTree> {

        if (!this.authService.isUserLoggedIn()) {
            this.redirectToLogin();
            return false;
        }

        if (!permissionData) {
            return true;
        }

        if (Array.isArray(permissionData.deniedRoles) && this.authService.isUserInRoles(permissionData.deniedRoles)) {
            return this.createAccessDeniedUrlTree(returnUrl);
        }

        if (Array.isArray(permissionData.deniedRoles) && Array.isArray(permissionData.permittedRoles)) {
            throw new Error('Don\'t set both \'deniedRoles\' and \'permittedRoles\' in route data.');
        }

        const target = permissionData.screenCode || permissionData.menuId;
        if (!target && Array.isArray(permissionData.permittedRoles)) {
            const permittedByRole = this.authService.isUserInRoles(permissionData.permittedRoles);
            return permittedByRole ? true : this.createAccessDeniedUrlTree(returnUrl);
        }

        if (!target) {
            return true;
        }

        return this.permissionService.loadCurrent().pipe(
            map(() => {
                const permitted = this.permissionService.canViewSync(target);
                const menuFallback = permissionData.menuId
                    ? this.authService.isAuthenticatedMenu(permissionData.menuId)
                    : false;
                const roleFallback = permitted === null && (
                    menuFallback || this.authService.isUserInRoles(permissionData.permittedRoles || [])
                );
                const result = permitted === true || roleFallback;

                return result ? true : this.createAccessDeniedUrlTree(returnUrl);
            })
        );
    }

    private getPermissionData(data: Record<string, any> | undefined): IGuardPermission {
        return (data?.[this.permissionObjectKey] || data?.[this.legacyPermissionObjectKey]) as IGuardPermission;
    }

    private redirectToLogin(): void {
        void navigateToPublicEntry(this.router, { replaceUrl: true });
    }

    private createAccessDeniedUrlTree(returnUrl: string): UrlTree {
        return this.router.createUrlTree(['/accessdenied'], { queryParams: { returnUrl } });
    }




}
